import React, { useEffect, useRef, useState } from "react";
import { getTokens } from "./auth/keycloakClient";
import logo2 from "./logo2.svg";
import RoleParser from "./roleParser";
import CostParser from "./costParser";
import ExpandButton from "./components/ExpandButton";
import CopyButton from "./components/CopyButton";
import { InfoIcon, ChipIcon } from "./components/Icons";
import CompartmentList from "./components/CompartmentList";
import MarkdownDisplay from "./components/MarkdownDisplay";



import { EXECUTE_URL, STAFFING_QUERY, TEXTAREA_CONFIG } from "./config/constants";
import { PANEL_THEMES } from "./config/themes";


export default function App() {
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // Separate states for structured output
  const [analysis, setAnalysis] = useState("");
  const [staffingText, setStaffingText] = useState("");
  const [costCode, setCostCode] = useState("");
  const [savedCostCode, setSavedCostCode] = useState("");
  const [showCostAndTools, setShowCostAndTools] = useState(false);
  const [isPrintingCost, setIsPrintingCost] = useState(false);
  const [isGeneratingCost, setIsGeneratingCost] = useState(false);

  // Tools & Certificates state
  const [toolsContent, setToolsContent] = useState("");

  // Dynamic staffed roles and expansion states
  const [dynamicStaffedRoles, setDynamicStaffedRoles] = useState([]);
  const [expandedStaffedRoles, setExpandedStaffedRoles] = useState({});

  // Dynamic cost compartments and expansion states
  const [dynamicCostCompartments, setDynamicCostCompartments] = useState([]);
  const [expandedCostCompartments, setExpandedCostCompartments] = useState({});

  // Click expansion state
  const [expandedPanel, setExpandedPanel] = useState(null);

  // Streaming toggle
  const [isStreamingEnabled, setIsStreamingEnabled] = useState(true);

  // Resizable panels state (Cost panel width in percentage)
  const [costPanelWidth, setCostPanelWidth] = useState(75);
  const isResizingRef = useRef(false);
  const containerRef = useRef(null);

  const currentSectionRef = useRef("analysis");
  const textBufferRef = useRef("");

  const textareaRef = useRef(null);
  const roleParser = useRef(null);
  const costParser = useRef(null);
  const abortRef = useRef(null);
  const scrollRefAnalysis = useRef(null);
  const scrollRefStaffedRoles = useRef(null);
  const scrollRefCode = useRef(null);

  // Initialize Parsers
  useEffect(() => {
    roleParser.current = new RoleParser();
    costParser.current = new CostParser();
  }, []);

  // Auto-resize textarea function
  const autoResizeTextarea = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const newHeight = Math.min(textarea.scrollHeight, TEXTAREA_CONFIG.MAX_HEIGHT);
      textarea.style.height = newHeight + 'px';

      // Show scrollbar only when content exceeds max height
      textarea.style.overflowY = textarea.scrollHeight > TEXTAREA_CONFIG.MAX_HEIGHT ? 'auto' : 'hidden';
    }
  };

  // Handle question change with auto-resize
  const handleQuestionChange = (e) => {
    setQuestion(e.target.value);
    setTimeout(autoResizeTextarea, 0); // Run after state update
  };

  // Handle panel click to expand/collapse
  const handlePanelClick = (panelId) => {
    setExpandedPanel(expandedPanel === panelId ? null : panelId);
  };

  // Keep each panel scrolled to bottom while streaming
  useEffect(() => {
    if (scrollRefAnalysis.current) {
      scrollRefAnalysis.current.scrollTop = scrollRefAnalysis.current.scrollHeight;
    }
  }, [analysis, loading]);

  useEffect(() => {
    if (scrollRefStaffedRoles.current) {
      scrollRefStaffedRoles.current.scrollTop = scrollRefStaffedRoles.current.scrollHeight;
    }
  }, [staffingText, loading]);

  useEffect(() => {
    if (scrollRefCode.current) {
      scrollRefCode.current.scrollTop = scrollRefCode.current.scrollHeight;
    }
  }, [costCode, loading]);

  const handleStreamOutput = (outputText, isNewMsg = false) => {
    if (isNewMsg) {
      setAnalysis("");
      setStaffingText("");
      setCostCode("");
      setDynamicStaffedRoles([]);
      setDynamicCostCompartments([]);
      setIsGeneratingCost(false);
      currentSectionRef.current = "analysis";
      textBufferRef.current = "";

      // Reset parsers
      if (roleParser.current) {
        roleParser.current.resetState();
      }
      if (costParser.current) {
        costParser.current.resetState();
      }
      return;
    }

    if (!outputText) return;

    // Add new text to buffer
    textBufferRef.current += outputText;
    let bufferContent = textBufferRef.current;

    // Define markers as single words
    const ANALYSIS_MARKER = "ENDOFANALYSIS";
    const STAFFING_MARKER = "ENDOFSTAFFING";
    const COSTANALYSIS_MARKER = "ENDOFCOSTANALYSIS";
    
    // Check for ENDOFANALYSIS marker
    if (currentSectionRef.current === "analysis") {
      const analysisIndex = bufferContent.indexOf(ANALYSIS_MARKER);

      if (analysisIndex !== -1) {
        
        // Get text before and after marker
        const beforeMarker = bufferContent.substring(0, analysisIndex).trim();
        const afterMarkerStart = analysisIndex + ANALYSIS_MARKER.length;
        const afterMarker = bufferContent.substring(afterMarkerStart).trim();
        
        // Add everything before marker to analysis
        if (beforeMarker) {
          setAnalysis((prev) => prev + beforeMarker);
        }

        // Switch to staffing section
        currentSectionRef.current = "staffing";
        
        // Set the buffer to the text after marker
        textBufferRef.current = afterMarker;
        
        // If there's text after marker, process it immediately
        if (afterMarker && afterMarker.length > 0) {
          setTimeout(() => {
            if (currentSectionRef.current === "staffing" && textBufferRef.current) {
              // Check if the afterMarker contains ENDOFSTAFFING already
              const staffingIndex = textBufferRef.current.indexOf(STAFFING_MARKER);
              if (staffingIndex === -1) {
                // No second marker yet, add all to staffing
                setStaffingText((prev) => prev + textBufferRef.current);
                textBufferRef.current = "";
              } else {
                // Second marker found immediately, process it
                handleStreamOutput("", false);
              }
            }
          }, 10);
        }
        
        return;
      }
    }
    
    // Check for ENDOFSTAFFING marker
    if (currentSectionRef.current === "staffing") {
      const staffingIndex = bufferContent.indexOf(STAFFING_MARKER);

      if (staffingIndex !== -1) {

        // Get text before and after marker
        const beforeMarker = bufferContent.substring(0, staffingIndex).trim();
        const afterMarkerStart = staffingIndex + STAFFING_MARKER.length;
        const afterMarker = bufferContent.substring(afterMarkerStart).trim();

        // Add everything before marker to staffing
        if (beforeMarker) {
          setStaffingText((prev) => prev + beforeMarker);

          // Process through parser for compartments
          if (roleParser.current) {
            const response = roleParser.current.processStreamContent({
              content: beforeMarker,
              section: "staffing"
            });

            if (response.isSuccessful && Array.isArray(response.content)) {
              setDynamicStaffedRoles(response.content.filter(role =>
                role && role.id && typeof role.id === 'string'
              ));
            }
          }
        }
        // Switch to cost section
        currentSectionRef.current = "cost";
        setIsGeneratingCost(true);

        // Set buffer to text after marker
        textBufferRef.current = afterMarker;

        // If there's text after marker, save it but don't display
        if (afterMarker && afterMarker.length > 0) {
          setTimeout(() => {
            if (currentSectionRef.current === "cost" && textBufferRef.current) {
              setSavedCostCode((prev) => prev + textBufferRef.current);
              textBufferRef.current = "";
            }
          }, 10);
        }
        
        return;
      }
    }

    // Check for ENDOFCOSTANALYSIS marker
    if (currentSectionRef.current === "cost") {
      const costAnalysisIndex = bufferContent.indexOf(COSTANALYSIS_MARKER);

      if (costAnalysisIndex !== -1) {

        // Get text before and after marker
        const beforeMarker = bufferContent.substring(0, costAnalysisIndex).trim();
        const afterMarkerStart = costAnalysisIndex + COSTANALYSIS_MARKER.length;
        const afterMarker = bufferContent.substring(afterMarkerStart).trim();

        // Add everything before marker to saved cost code
        if (beforeMarker) {
          setSavedCostCode((prev) => prev + beforeMarker);
        }

        // Switch to tools section
        currentSectionRef.current = "tools";

        // Set buffer to text after marker
        textBufferRef.current = afterMarker;

        // If there's text after marker, save it to tools
        if (afterMarker && afterMarker.length > 0) {
          setTimeout(() => {
            if (currentSectionRef.current === "tools" && textBufferRef.current) {
              setToolsContent((prev) => prev + textBufferRef.current);
              textBufferRef.current = "";
            }
          }, 10);
        }

        return;
      }
    }

    // Check if we might be building a marker (partial detection)
    // With single-word markers, we need simpler partial detection
    const partialMarkers = [
      "ENDOFANALYSI", "ENDOFANALYS", "ENDOFANALY", "ENDOFANAL",
      "ENDOFANA", "ENDOFAN", "ENDOFA", "ENDOF", "ENDO", "END",
      "ENDOFSTAFFIN", "ENDOFSTAFFI", "ENDOFSTAFF", "ENDOFSTAF",
      "ENDOFSTA", "ENDOFST",
      "ENDOFCOSTANALYSI", "ENDOFCOSTANALYS", "ENDOFCOSTANALY", "ENDOFCOSTANAL",
      "ENDOFCOSTANA", "ENDOFCOSTAN", "ENDOFCOSTA", "ENDOFCOST",
      "ENDOFCOS", "ENDOFCO", "ENDOFC"
    ];
    
    let mightBeMarker = false;
    for (const partial of partialMarkers) {
      if (bufferContent.endsWith(partial)) {
        mightBeMarker = true;
        break;
      }
    }
    
    if (!mightBeMarker) {
      // No marker or partial marker - flush buffer to current section
      if (currentSectionRef.current === "analysis") {
        if (isStreamingEnabled) {
          setAnalysis((prev) => prev + bufferContent);
        }
      } else if (currentSectionRef.current === "staffing") {
        if (isStreamingEnabled) {
          setStaffingText((prev) => prev + bufferContent);

          // Process through parser for compartments
          if (roleParser.current) {
            const response = roleParser.current.processStreamContent({
              content: bufferContent,
              section: "staffing"
            });

            if (response.isSuccessful && Array.isArray(response.content)) {
              setDynamicStaffedRoles(response.content.filter(role =>
                role && role.id && typeof role.id === 'string'
              ));
            }
          }
        }
      } else if (currentSectionRef.current === "cost") {
        // Cost estimation ALWAYS just saves content, NEVER displays live
        // No matter if chat or button - always collect, never stream
        setSavedCostCode((prev) => prev + bufferContent);
      } else if (currentSectionRef.current === "tools") {
        // Tools & Certificates content - just save, display only when button pressed
        setToolsContent((prev) => prev + bufferContent);
      }
      // Only clear buffer when streaming is enabled - when OFF, keep accumulating for marker detection
      // Exception: always clear cost and tools buffer since they're already saved
      if (isStreamingEnabled || currentSectionRef.current === "cost" || currentSectionRef.current === "tools") {
        textBufferRef.current = "";
      }
    }
    // If mightBeMarker is true, keep text in buffer and wait for more
  };

  // Generic Stream-Handler function
  const handleStream = async (query) => {
    if (loading) return;

    // Immediate content refresh - clear everything to initial state
    setAnalysis("");
    setStaffingText("");
    setCostCode("");
    setSavedCostCode("");
    setToolsContent("");
    setDynamicStaffedRoles([]);
    setExpandedStaffedRoles({});
    setDynamicCostCompartments([]);
    setExpandedCostCompartments({});
    setShowCostAndTools(false);  // Always reset showCostAndTools
    setIsGeneratingCost(false);
    setErr(null);
    setLoading(true);
    currentSectionRef.current = "analysis";
    textBufferRef.current = "";

    // Reset parser
    if (roleParser.current) {
      roleParser.current.resetState();
    }

    try {
      const { access_token, refresh_token } = await getTokens();
      const body = {
        query: query,
        webhook_url: null,
        refresh_token: refresh_token || "",
        context_building_mode: "auto",
        execute_with_code_generation: false,
      };

      const controller = new AbortController();
      abortRef.current = controller;

      const resp = await fetch(EXECUTE_URL, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${access_token}`,
          "Content-Type": "application/json",
          Accept: "text/event-stream, application/x-ndjson, application/json",
        },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

      if (!resp.ok || !resp.body) {
        const text = await resp.text().catch(() => "");
        throw new Error(`HTTP ${resp.status} ${resp.statusText}${text ? " - " + text : ""}`);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";

      const handleEventObject = (obj) => {
        const diana = obj?.diana_agent_execution;
        if (diana) {
          const output = diana.output ?? "";
          handleStreamOutput(output, !!diana.is_beginning_of_new_message);
        }

        if (Array.isArray(obj?.execution_layers)) {
          for (const layer of obj.execution_layers) {
            const execs = layer?.agent_executions || [];
            for (const ex of execs) {
              if (typeof ex?.output === "string" && ex.output.length > 0) {
                handleStreamOutput(ex.output, false);
              }
            }
          }
        }
      };

      const processNdjsonLine = (line) => {
        const s = line.trim();
        if (!s || s === "[DONE]") return;
        try {
          handleEventObject(JSON.parse(s));
        } catch {
          /* ignore */
        }
      };

      const processSseBlock = (block) => {
        const lines = block.split(/\r?\n/);
        const dataLines = [];
        for (const l of lines) if (l.startsWith("data:")) dataLines.push(l.slice(5).trim());
        if (!dataLines.length) return false;
        const payload = dataLines.join("\n").trim();
        if (!payload || payload === "[DONE]") return true;
        try {
          handleEventObject(JSON.parse(payload));
          return true;
        } catch {
          return false;
        }
      };

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        let sseParts = buffer.split(/\r?\n\r?\n/);
        buffer = sseParts.pop() ?? "";
        for (const blk of sseParts) {
          const handled = processSseBlock(blk);
          if (!handled) blk.split(/\r?\n/).forEach(processNdjsonLine);
        }

        const ndjsonParts = buffer.split(/\r?\n/);
        buffer = ndjsonParts.pop() ?? "";
        ndjsonParts.forEach(processNdjsonLine);
      }

      if (buffer.trim()) {
        if (!processSseBlock(buffer)) processNdjsonLine(buffer);
      }
      
      // Final cleanup - flush any remaining buffer content
      if (textBufferRef.current) {
        const remaining = textBufferRef.current;
        if (currentSectionRef.current === "analysis") {
          setAnalysis((prev) => prev + remaining);
        } else if (currentSectionRef.current === "staffing") {
          setStaffingText((prev) => prev + remaining);
        } else if (currentSectionRef.current === "cost") {
          // Just save the content, NEVER auto-display compartments from chat
          setSavedCostCode((prev) => prev + remaining);
        }
        textBufferRef.current = "";
      }
      
    } catch (e) {
      if (e.name !== "AbortError") {
        console.error(e);
        setErr(e.message || "Streaming failed. Check Keycloak token / execute URL.");
      }
    } finally {
      setLoading(false);
      setIsGeneratingCost(false);
      abortRef.current = null;
    }
  };

  const ask = async () => {
    const q = question.trim();
    if (!q || loading) return;
    await handleStream(q);
  };

  const generateStaffing = () => {
    handleStream(STAFFING_QUERY);
  };

  const generateCostAndTools = () => {
    if (savedCostCode && !isPrintingCost) {
      setIsPrintingCost(true);
      setCostCode("");
      setDynamicCostCompartments([]); // Clear compartments first
      setShowCostAndTools(true);

      // Process compartments and display content - same for both modes
      if (costParser.current) {
        costParser.current.resetState();

        const response = costParser.current.processStreamContent({
          content: savedCostCode,
          section: "cost"
        });

        if (response.isSuccessful && Array.isArray(response.content)) {
          setDynamicCostCompartments(response.content.filter(compartment =>
            compartment && compartment.id && typeof compartment.id === 'string'
          ));
        }
      }

      // Always display content instantly for Cost estimation
      setCostCode(savedCostCode);
      setIsPrintingCost(false);
    }
  };

  const cancel = () => abortRef.current?.abort();

  // Resizer handlers
  const handleMouseDown = (e) => {
    e.preventDefault();
    isResizingRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const handleMouseMove = (e) => {
    if (!isResizingRef.current || !containerRef.current) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const offsetX = e.clientX - containerRect.left;
    const newWidth = (offsetX / containerRect.width) * 100;

    // Limit between 20% and 80%
    if (newWidth >= 20 && newWidth <= 80) {
      setCostPanelWidth(newWidth);
    }
  };

  const handleMouseUp = () => {
    isResizingRef.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  };

  // Add global mouse listeners for resize
  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const clearAll = () => {
    setQuestion("");
    setAnalysis("");
    setStaffingText("");
    setCostCode("");
    setSavedCostCode("");
    setToolsContent("");
    setShowCostAndTools(false);
    setIsPrintingCost(false);
    setIsGeneratingCost(false);
    setDynamicStaffedRoles([]);
    setExpandedStaffedRoles({});
    setDynamicCostCompartments([]);
    setExpandedCostCompartments({});
    setErr(null);
    currentSectionRef.current = "analysis";
    textBufferRef.current = "";

    // Reset parser
    if (roleParser.current) {
      roleParser.current.resetState();
    }
  };

  return (
    <div className="min-h-screen w-full bg-gray-50 text-gray-900">
      <div className="mx-auto px-4 py-4 h-screen">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[calc(100vh-2rem)]">
          {/* LEFT: typing area (no inner scroll) */}
          <div className="bg-white rounded-2xl shadow-md flex flex-col">
            <div className="px-5 py-3 border-b">
              <h1 className="text-xl font-semibold"><span className="font-mono bg-amber-100 text-gray-800 px-2 py-1 rounded">Staffing-AI</span> Query Intake</h1>
            </div>
            <div className="p-5 flex-1 flex flex-col">

            <div className="space-y-3">
              {/* Staffing-AI compartment */}
              <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-visible">
                <div className="bg-gradient-to-r from-amber-100 to-amber-50 px-4 py-2 border-b border-gray-200">
                  <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                    <ChipIcon className="w-5 h-5 text-amber-500" />
                    <span style={{marginTop: '-3px'}}>Role Extraction, Staffing and cost estimation</span>
                  </h3>
                </div>
                <div className="p-4">
                <div className="flex flex-col space-y-4">
                  <div className="relative group">
                    <button
                      onClick={generateStaffing}
                      disabled={loading}
                      className="flex items-center justify-center gap-2 rounded-lg bg-amber-200 px-4 py-2 text-gray-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-amber-300 transition text-center w-full"
                    >
                      Project: Role Extraction and Staffing
                    </button>
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-[60]">
                      <div className="text-center">
                        <div>Analyzes project documentation to identify required roles,</div>
                        <div>matches qualified employees based on skills and availability,</div>
                        <div>and provides alternative staffing options.</div>
                      </div>
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                    </div>
                  </div>
                  <div className="border-t border-gray-300 my-4 -mx-4"></div>
                  <div className="relative group">
                    <button
                      onClick={generateCostAndTools}
                      disabled={loading || isPrintingCost || !savedCostCode}
                      className="flex items-center justify-center gap-2 rounded-lg bg-amber-200 px-4 py-2 text-gray-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-amber-300 transition text-center w-full"
                    >
                      {isPrintingCost && (
                        <svg
                          className="animate-spin h-4 w-4"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 818-8v4a4 4 0 00-4 4H4z" />
                        </svg>
                      )}
                      Cost and Tools
                    </button>
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-[60]">
                      <div className="text-center">
                        <div>Displays cost estimation and required tools for the project.</div>
                      </div>
                      <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                    </div>
                  </div>
                </div>
                </div>
              </div>

              <div className="border-t border-gray-200 -mx-5"></div>

              {/* Chat input */}
              <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-visible">
                <div className="bg-gradient-to-r from-amber-100 to-amber-50 px-4 py-2 border-b border-gray-200">
                  <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                    <svg className="w-5 h-5 text-amber-500" fill="currentColor" viewBox="0 0 24 24">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" />
                      <text x="12" y="16" fontFamily="Arial, sans-serif" fontSize="12" fontWeight="bold" textAnchor="middle" fill="currentColor">?</text>
                    </svg>
                    <span style={{marginTop: '-3px'}}>Your question</span>
                  </h3>
                </div>
                <div className="p-0">
                <textarea
                  ref={textareaRef}
                  id="question"
                  value={question}
                  onChange={handleQuestionChange}
                  placeholder="Type your question here..."
                  rows="2"
                  className="w-full resize-y rounded-b-xl border-0 p-3 focus:outline-none focus:ring-0 block"
                  style={{
                    minHeight: TEXTAREA_CONFIG.MIN_HEIGHT,
                    maxHeight: `${TEXTAREA_CONFIG.MAX_HEIGHT}px`,
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'rgb(156 163 175) transparent',
                    display: 'block',
                    marginBottom: '0'
                  }}
                />
                </div>
              </div>

              <div className="border-t border-gray-200 -mx-5"></div>

              {/* Send, Clear and Cancel buttons row */}
              <div className="flex gap-2 justify-end">
                <button
                  onClick={ask}
                  disabled={loading || question.trim().length === 0}
                  className="inline-flex items-center gap-2 rounded-lg bg-amber-200 px-4 py-2 text-gray-800 font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-amber-300 transition"
                >
                  {loading && (
                    <svg
                      className="animate-spin h-4 w-4"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                    </svg>
                  )}
                  Send
                </button>
                <button
                  onClick={clearAll}
                  disabled={question.trim().length === 0}
                  className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-red-700 transition"
                >
                  Clear
                </button>
                {loading && (
                  <button
                    onClick={cancel}
                    className="inline-flex items-center gap-2 rounded-lg bg-gray-600 px-4 py-2 text-white font-medium hover:bg-gray-700 transition"
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>

            {err && <p className="mt-3 text-sm text-red-600">{err}</p>}

            <div className="mt-auto pt-4 flex justify-end">
              <img
                src={logo2}
                alt="Company logo 2"
                className="h-8 w-auto opacity-80 select-none pointer-events-none"
              />
            </div>
            </div>
          </div>

          {/* RIGHT: Three separate output panels */}
          <div className="bg-white rounded-2xl shadow-md flex flex-col h-full overflow-hidden lg:col-span-2">
            <div className="px-5 py-3 border-b flex items-center justify-between">
              <h2 className="text-xl font-semibold"><span className="font-mono bg-amber-100 text-gray-800 px-2 py-1 rounded">Staffing-AI</span> Generative Output</h2>
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-700 leading-none -translate-y-0.5">Streaming</span>
                <button
                  onClick={() => setIsStreamingEnabled(!isStreamingEnabled)}
                  disabled={loading || isPrintingCost}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    isStreamingEnabled ? 'bg-blue-600' : 'bg-gray-300'
                  } ${(loading || isPrintingCost) ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      isStreamingEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            <div className="flex-1 p-5 flex flex-col gap-3 overflow-hidden min-h-0 relative">
              {/* Analysis Panel */}
              <div
                className={`${PANEL_THEMES.analysis.background} rounded-xl flex-1 flex flex-col overflow-hidden min-h-0 ${
                  expandedPanel === 'analysis'
                    ? 'absolute top-5 left-5 right-5 bottom-5 z-10 shadow-2xl transition-all duration-700'
                    : expandedPanel && expandedPanel !== 'analysis'
                    ? 'opacity-30 transition-opacity duration-0'
                    : 'transition-all duration-0'
                }`}
              >
                <div className="relative">
                  <div className={`px-4 py-2 ${PANEL_THEMES.analysis.header} rounded-t-xl`}>
                    <h3 className={`font-semibold ${PANEL_THEMES.analysis.title} flex items-center gap-2`}>
                      {PANEL_THEMES.analysis.icon} Analysis of the incoming Project PDF
                      <InfoIcon
                        theme="orange"
                        tooltip="Analyzes the incoming project PDF and provides detailed insights into requirements, scope, and staffing needs."
                      />
                    </h3>
                  </div>
                  <ExpandButton panelId="analysis" isExpanded={expandedPanel === 'analysis'} onToggle={handlePanelClick} theme="orange" />
                  <CopyButton
                    content={analysis}
                    theme="orange"
                    className="absolute top-1/2 right-12 -translate-y-1/2"
                  />
                </div>
                <div className="flex-1 px-4 pb-4 overflow-y-auto min-h-0" ref={scrollRefAnalysis}>
                  <div className="text-sm pt-2">
                    <MarkdownDisplay content={analysis} className="text-sm" />
                  </div>

                  {/* Debug info */}
                  {loading && !analysis && (
                    <div className="text-gray-500 text-sm pt-2 italic">
                      Waiting for analysis content...
                    </div>
                  )}
                </div>
              </div>

              {/* Staffed Roles Panel */}
              <div
                className={`${PANEL_THEMES.staffing.background} rounded-xl flex-1 flex flex-col overflow-hidden min-h-0 ${
                  expandedPanel === 'staffing'
                    ? 'absolute top-5 left-5 right-5 bottom-5 z-10 shadow-2xl transition-all duration-700'
                    : expandedPanel && expandedPanel !== 'staffing'
                    ? 'opacity-30 transition-opacity duration-0'
                    : 'transition-all duration-0'
                }`}
              >
                <div className="relative">
                  <div className={`px-4 py-2 ${PANEL_THEMES.staffing.header} rounded-t-xl`}>
                    <h3 className={`font-semibold ${PANEL_THEMES.staffing.title} flex items-center gap-2`}>
                      {PANEL_THEMES.staffing.icon} Staffed Roles
                      <InfoIcon
                        theme="amber"
                        tooltip="Displays the employees and alternatives staffed to the roles extracted from the PDF."
                      />
                    </h3>
                  </div>
                  <ExpandButton panelId="staffing" isExpanded={expandedPanel === 'staffing'} onToggle={handlePanelClick} theme="amber" />
                </div>
                <div className="flex-1 px-4 pb-4 overflow-y-auto min-h-0" ref={scrollRefStaffedRoles}>
                  {/* Dynamic SUB-compartments within the green box */}
                  <CompartmentList
                    items={dynamicStaffedRoles}
                    expandedState={expandedStaffedRoles}
                    setExpandedState={setExpandedStaffedRoles}
                    showOnButtonPress={false}
                    defaultExpanded={true}
                    theme="amber"
                    icon="👤"
                    loadingMessage="Waiting for staffed roles..."
                    loading={loading && dynamicStaffedRoles.length === 0 && !staffingText}
                  />

                  {/* No raw text display - only compartments */}

                </div>
              </div>

              {/* Bottom Row: Two equal panels side by side */}
              <div ref={containerRef} className="flex flex-1 min-h-0">
                {/* Left: Cost estimation Panel */}
                <div
                  style={expandedPanel !== 'cost' ? { width: `${costPanelWidth}%` } : {}}
                  className={`${PANEL_THEMES.cost.background} rounded-xl flex flex-col overflow-hidden min-h-0 ${
                    expandedPanel === 'cost'
                      ? 'absolute top-5 left-5 right-5 bottom-5 z-10 shadow-2xl transition-all duration-700'
                      : expandedPanel && expandedPanel !== 'cost'
                      ? 'opacity-30 transition-opacity duration-0'
                      : 'transition-all duration-0'
                  }`}
                >
                  <div className="relative">
                    <div className={`px-4 py-2 ${PANEL_THEMES.cost.header} rounded-t-xl`}>
                      <h3 className={`font-semibold ${PANEL_THEMES.cost.title} flex items-center gap-2`}>
                        {PANEL_THEMES.cost.icon} Cost estimation
                        <InfoIcon
                          theme="gray"
                          tooltip="Displays cost estimation for the staffed roles"
                        />
                      </h3>
                    </div>
                    <ExpandButton panelId="cost" isExpanded={expandedPanel === 'cost'} onToggle={handlePanelClick} theme="gray" />
                  </div>
                  <div className="flex-1 px-4 pb-4 overflow-y-auto min-h-0" ref={scrollRefCode}>
                    {/* Dynamic cost compartments - only show when print button was pressed */}
                    <CompartmentList
                      items={dynamicCostCompartments}
                      expandedState={expandedCostCompartments}
                      setExpandedState={setExpandedCostCompartments}
                      showOnButtonPress={true}
                      buttonPressed={showCostAndTools}
                      defaultExpanded={false}
                      theme="gray"
                      icon="🧾"
                      loading={false}
                    />

                    {/* Fallback to raw code display if no compartments or showCostAndTools is false */}
                    {(!showCostAndTools || !Array.isArray(dynamicCostCompartments) || dynamicCostCompartments.length === 0) && (
                      <div className="text-gray-900 text-sm overflow-auto p-3 mt-2">
                        {costCode ? (
                          <MarkdownDisplay content={costCode} className="text-sm" />
                        ) : (savedCostCode && !loading && !showCostAndTools ? "Cost estimation data can be displayed." :
                          (isGeneratingCost ? "Cost estimation is generating!" :
                           "No cost estimation data available."))}
                      </div>
                    )}

                  </div>
                </div>

                {/* Resizable Divider */}
                <div
                  onMouseDown={handleMouseDown}
                  className="bg-gray-200 hover:bg-gray-300 cursor-col-resize flex-shrink-0 transition-colors duration-150 mx-1.5 self-center rounded-full"
                  style={{ width: '2px', minWidth: '2px', height: '95%' }}
                />

                {/* Right: Tools & Certificates Panel */}
                <div
                  style={expandedPanel !== 'tools' ? { width: `${100 - costPanelWidth}%` } : {}}
                  className={`${PANEL_THEMES.tools.background} rounded-xl flex flex-col overflow-hidden min-h-0 ${
                    expandedPanel === 'tools'
                      ? 'absolute top-5 left-5 right-5 bottom-5 z-10 shadow-2xl transition-all duration-700'
                      : expandedPanel && expandedPanel !== 'tools'
                      ? 'opacity-30 transition-opacity duration-0'
                      : 'transition-all duration-0'
                  }`}
                >
                  <div className="relative">
                    <div className={`px-4 py-2 ${PANEL_THEMES.tools.header} rounded-t-xl`}>
                      <h3 className={`font-semibold ${PANEL_THEMES.tools.title} flex items-center gap-2`}>
                        {PANEL_THEMES.tools.icon} Tools & Certificates
                        <InfoIcon
                          theme="gray"
                          tooltip="Displays required tools and certificates"
                        />
                      </h3>
                    </div>
                    <ExpandButton panelId="tools" isExpanded={expandedPanel === 'tools'} onToggle={handlePanelClick} theme="gray" />
                    <CopyButton
                      content={toolsContent}
                      theme="gray"
                      className="absolute top-1/2 right-12 -translate-y-1/2"
                    />
                  </div>
                  <div className="flex-1 px-4 pb-4 overflow-y-auto min-h-0">
                    <div className="text-gray-900 text-sm overflow-auto p-3 mt-2">
                      {showCostAndTools && toolsContent ? (
                        <MarkdownDisplay content={toolsContent} className="text-sm" />
                      ) : (toolsContent && !showCostAndTools
                          ? "Tools & Certificates data can be displayed."
                          : (isGeneratingCost
                            ? "Tools & Certificates data is generating!"
                            : "No tools & certificates data available."))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}